import React from 'react';
import {
  DndContext, closestCenter,
  useSensor, useSensors, PointerSensor, KeyboardSensor
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Fila sortable de la tabla de items.
 *
 * @param {Object} props
 * @param {Object} props.item - Datos del item
 * @param {Function} props.onEdit - Handler editar
 * @param {Function} props.onDelete - Handler eliminar
 * @param {boolean} props.isEditable - Si puede editar
 * @param {boolean} props.isDeletable - Si puede eliminar
 * @returns {JSX.Element}
 */
function SortableRow({ item, onEdit, onDelete, isEditable, isDeletable }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''} {...attributes}>
      {isEditable && (
        <td className="drag-handle" {...listeners}>
          <i className="fas fa-grip-vertical"></i>
        </td>
      )}
      <td className="text-center">{item.orden}</td>
      <td className="text-center"><span className="badge badge-secondary">{item.tipo}</span></td>
      <td>{item.descripcion}</td>
      <td className="text-center">{parseFloat(item.cantidad).toFixed(2)}</td>
      <td className="text-center">{item.unidad}</td>
      <td className="text-right">${parseFloat(item.precio_unitario).toLocaleString('es-CL')}</td>
      <td className="text-right"><strong>${parseFloat(item.subtotal).toLocaleString('es-CL')}</strong></td>
      {(isEditable || isDeletable) && (
        <td className="text-center">
          <div className="table-action-buttons">
            {isEditable && (
              <button className="btn btn-table-action btn-ghost-primary" onClick={() => onEdit(item)} title="Editar">
                <i className="fas fa-edit"></i>
              </button>
            )}
            {isDeletable && (
              <button className="btn btn-table-action btn-ghost-danger" onClick={() => onDelete(item)} title="Eliminar">
                <i className="fas fa-trash"></i>
              </button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

/**
 * Tabla de items de cotización con drag & drop para reordenar.
 *
 * @param {Object} props
 * @param {Array} props.items - Lista de items
 * @param {boolean} props.canEdit - Si puede editar items
 * @param {boolean} props.canDelete - Si puede eliminar items
 * @param {boolean} props.isReordering - Si está reordenando
 * @param {Function} props.onDragEnd - Handler drag end
 * @param {Function} props.onCreate - Handler crear item
 * @param {Function} props.onEdit - Handler editar item
 * @param {Function} props.onDelete - Handler eliminar item
 * @param {React.ReactNode} [props.children] - Contenido adicional dentro de la card (ej: totales)
 * @returns {JSX.Element}
 */
function CotizacionItemsTable({
  items, canEdit, canDelete, isReordering,
  onDragEnd, onCreate, onEdit, onDelete, children
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <div className="card">
      <div className="display-flex justify-between align-center margin-bottom-md">
        <h2>Items de la Cotización</h2>
        {canEdit && (
          <button className="btn btn-primary" onClick={onCreate}>
            <i className="fas fa-plus"></i> Agregar Item
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <p>No hay items en esta cotización</p>
          {canEdit && <button className="btn btn-primary" onClick={onCreate}>Agregar Primer Item</button>}
        </div>
      ) : (
        <div className="table-responsive">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <table className="table table-hover">
              <thead>
                <tr>
                  {canEdit && <th style={{ width: '3%' }}></th>}
                  <th className="text-center" style={{ width: '5%' }}>Orden</th>
                  <th className="text-center" style={{ width: '10%' }}>Tipo</th>
                  <th style={{ width: '35%' }}>Descripción</th>
                  <th className="text-center" style={{ width: '10%' }}>Cantidad</th>
                  <th className="text-center" style={{ width: '10%' }}>Unidad</th>
                  <th className="text-right" style={{ width: '12%' }}>Precio Unit.</th>
                  <th className="text-right" style={{ width: '13%' }}>Subtotal</th>
                  {(canEdit || canDelete) && <th className="text-center" style={{ width: '5%' }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy} disabled={!canEdit || isReordering}>
                  {items.map(item => (
                    <SortableRow key={item.id} item={item} onEdit={onEdit} onDelete={onDelete} isEditable={canEdit} isDeletable={canDelete} />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </div>
      )}

      {children}
    </div>
  );
}

export default CotizacionItemsTable;
